import { useId, useState } from 'react';
import {
  BriefcaseIcon,
  CalendarIcon,
  EditIcon,
  GlobeIcon,
  PhoneIcon,
  PlusIcon,
  TimesheetIcon,
  TrashIcon,
} from '@/components/icons';
import { ActionIcon } from '@/components/ui/ActionIcon';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { Pagination } from '@/components/ui/Pagination';
import { Tabs } from '@/components/ui/Tabs';
import { useLeaveRequests } from '../useTimesheets';
import { formatDays, formatMonth } from '../timesheet-labels';
import type {
  Timesheet,
  TimesheetOptions,
  TimesheetOvertime,
  TimesheetOvertimeInput,
} from '../types';
import { TimesheetLeaveModal } from './TimesheetLeaveModal';
import { TimesheetOvertimeModal } from './TimesheetOvertimeModal';
import { extraDate, monthBounds, overtimeInput } from './timesheet-extras-utils';
import './TimesheetExtras.css';

type ExtraTab = 'internal' | 'absence' | 'overtime' | 'oncall' | 'travel';
const tabs = [
  { value: 'internal', label: 'Activité interne', icon: <BriefcaseIcon /> },
  { value: 'absence', label: 'Absence', icon: <CalendarIcon /> },
  { value: 'overtime', label: 'Heures supplémentaires', icon: <TimesheetIcon /> },
  { value: 'oncall', label: 'Astreintes', icon: <PhoneIcon /> },
  { value: 'travel', label: 'Déplacements', icon: <GlobeIcon /> },
] as const;

export const TimesheetExtras = ({
  timesheet,
  readOnly = false,
  options,
  pending,
  onOvertimesChange,
  view = 'all',
  isActive = true,
}: {
  timesheet: Timesheet;
  readOnly?: boolean;
  options: TimesheetOptions;
  pending: boolean;
  onOvertimesChange: (entries: TimesheetOvertimeInput[]) => Promise<void>;
  view?: 'all' | 'absence' | 'overtime' | 'other';
  isActive?: boolean;
}) => {
  const id = useId();
  const [selectedTab, setTab] = useState<ExtraTab>(view === 'other' ? 'internal' : 'absence');
  const tab = view === 'absence' || view === 'overtime' ? view : selectedTab;
  const navigationTabs =
    view === 'other' ? tabs.filter((item) => !['absence', 'overtime'].includes(item.value)) : tabs;
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<'leave' | 'overtime' | null>(null);
  const [editing, setEditing] = useState<TimesheetOvertime | null>(null);
  const [deleting, setDeleting] = useState<TimesheetOvertime | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [removing, setRemoving] = useState(false);
  const bounds = monthBounds(timesheet.periodMonth);
  const leaves = useLeaveRequests(
    { missionId: timesheet.mission.id, dateFrom: bounds.from, dateTo: bounds.to, page, limit: 5 },
    isActive && tab === 'absence',
  );
  const active = tabs.find((item) => item.value === tab)!;
  const available = tab === 'absence' || tab === 'overtime';
  const canEdit = timesheet.permissions.canEdit && !readOnly;
  const busy = pending || removing;
  return (
    <section
      className={`cra-extras ${view === 'all' ? '' : 'cra-extras--standalone'}`}
      aria-label={active.label}
    >
      {view === 'all' && (
        <div className="cra-extras__intro">
          <div>
            <span className="cra-eyebrow">En complément du calendrier</span>
            <h2>Déclarations complémentaires</h2>
          </div>
          <span>{formatMonth(timesheet.periodMonth)}</span>
        </div>
      )}
      {(view === 'all' || view === 'other') && (
        <Tabs
          id={id}
          size="sm"
          label="Déclarations complémentaires"
          className="cra-extras__tabs"
          tabs={navigationTabs}
          value={tab}
          onChange={setTab}
        />
      )}
      {/* Le panneau de la page fournit déjà la sémantique d’onglet pour les vues directes. */}
      <div
        role={view === 'all' || view === 'other' ? 'tabpanel' : undefined}
        id={`${id}-${tab}-panel`}
        aria-labelledby={view === 'all' || view === 'other' ? `${id}-${tab}` : undefined}
      >
        <div className="cra-extras__panel">
          <div className="cra-extras__heading">
            <div className="cra-extras__title">
              <span className="cra-extras__icon">{active.icon}</span>
              <div>
                <h3>{tab === 'absence' ? 'Demandes d’absence' : active.label}</h3>
                <p>
                  {available
                    ? timesheet.mission.title || timesheet.mission.projectName
                    : 'Déclaration complémentaire à la mission'}
                </p>
              </div>
            </div>
            {available ? (
              canEdit ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy || (tab === 'absence' && !options.leaveTypes.length)}
                  onClick={() => {
                    setEditing(null);
                    setModal(tab === 'absence' ? 'leave' : 'overtime');
                  }}
                >
                  <PlusIcon />
                  {tab === 'absence' ? 'Ajouter une absence' : 'Ajouter des heures'}
                </Button>
              ) : (
                <Badge>Lecture seule</Badge>
              )
            ) : (
              <Badge tone="muted">À venir</Badge>
            )}
          </div>
          {tab === 'absence' ? (
            <>
              {leaves.isPending ? (
                <p role="status" className="cra-extras__empty">
                  Chargement des demandes…
                </p>
              ) : leaves.isError ? (
                <div role="alert" className="cra-notice">
                  Les demandes n’ont pas pu être chargées.
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void leaves.refetch();
                    }}
                  >
                    Réessayer
                  </Button>
                </div>
              ) : leaves.data.items.length ? (
                <>
                  <ul className="cra-extras__list">
                    {leaves.data.items.map((leave) => (
                      <li key={leave.id} className="cra-extras__entry">
                        <span className="cra-extras__entry-icon">
                          <CalendarIcon />
                        </span>
                        <div className="cra-extras__entry-body">
                          <div className="cra-extras__entry-title">
                            <strong>{leave.type.label}</strong>
                            <Badge
                              tone={
                                leave.status.code === 'ACCEPTEE'
                                  ? 'success'
                                  : leave.status.code === 'REFUSEE'
                                    ? 'danger'
                                    : 'warning'
                              }
                            >
                              {leave.status.label}
                            </Badge>
                          </div>
                          <p>
                            {extraDate(leave.startDate)}
                            {leave.endDate !== leave.startDate && ` → ${extraDate(leave.endDate)}`}
                          </p>
                          {(leave.halfDayStart || leave.halfDayEnd) && (
                            <p>
                              {leave.startDate === leave.endDate
                                ? 'Demi-journée'
                                : [
                                    leave.halfDayStart && 'Début en demi-journée',
                                    leave.halfDayEnd && 'Fin en demi-journée',
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </p>
                          )}
                          {leave.comment && <p className="cra-extras__comment">{leave.comment}</p>}
                          {leave.rejectionReason && <p>Motif du refus : {leave.rejectionReason}</p>}
                        </div>
                        <span className="cra-extras__quantity">
                          {leave.days === null ? '—' : `${formatDays(leave.days)} j`}
                          <small>sur la période</small>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Pagination
                    meta={leaves.data.meta}
                    count={leaves.data.items.length}
                    loading={leaves.isFetching}
                    onPageChange={setPage}
                  />
                </>
              ) : (
                <div className="cra-extras__empty">
                  <strong>Aucune demande ce mois-ci</strong>
                  <p>
                    Les demandes qui concernent ce mois apparaîtront ici, avec leur statut de
                    validation.
                  </p>
                </div>
              )}
              {!options.leaveTypes.length && (
                <p className="cra-extra-form__hint">
                  Aucun type d’absence disponible pour la création.
                </p>
              )}
            </>
          ) : tab === 'overtime' ? (
            timesheet.overtimes.length ? (
              <>
                <ul className="cra-extras__list">
                  {timesheet.overtimes.map((entry) => (
                    <li key={entry.id} className="cra-extras__entry">
                      <span className="cra-extras__entry-icon">
                        <TimesheetIcon />
                      </span>
                      <div className="cra-extras__entry-body">
                        <div className="cra-extras__entry-title">
                          <strong>{extraDate(entry.date)}</strong>
                          {entry.type && <Badge>{entry.type.label}</Badge>}
                        </div>
                        <p>
                          {entry.startTime} → {entry.endTime}
                          {entry.endTime < entry.startTime ? ' · lendemain' : ''}
                          {entry.surchargeRate
                            ? ` · coefficient ×${formatDays(entry.surchargeRate)}`
                            : ''}
                        </p>
                        {entry.motive && <p className="cra-extras__comment">{entry.motive}</p>}
                      </div>
                      <span className="cra-extras__quantity">{formatDays(entry.hours)} h</span>
                      {canEdit && (
                        <div className="cra-extras__actions">
                          <ActionIcon
                            label={`Modifier les heures du ${extraDate(entry.date)} à ${entry.startTime}`}
                            disabled={busy}
                            onClick={() => {
                              setEditing(entry);
                              setModal('overtime');
                            }}
                          >
                            <EditIcon />
                          </ActionIcon>
                          <ActionIcon
                            label={`Supprimer les heures du ${extraDate(entry.date)} à ${entry.startTime}`}
                            disabled={busy}
                            onClick={() => {
                              setDeleteError('');
                              setDeleting(entry);
                            }}
                          >
                            <TrashIcon />
                          </ActionIcon>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="cra-extras__total">
                  <span>Total du mois</span>
                  <strong>{formatDays(timesheet.totals.overtimeHours)} h</strong>
                </div>
              </>
            ) : (
              <div className="cra-extras__empty">
                <strong>Aucune heure supplémentaire</strong>
                <p>
                  Renseignez une date et une plage horaire. La durée sera calculée automatiquement.
                </p>
              </div>
            )
          ) : (
            <div className="cra-extras__empty">
              <strong>La saisie sera bientôt disponible</strong>
              <p>
                {tab === 'internal'
                  ? 'Formation, réunions et autres activités internes.'
                  : tab === 'oncall'
                    ? 'Périodes d’astreinte et interventions associées.'
                    : 'Déplacements effectués dans le cadre de la mission.'}
              </p>
            </div>
          )}
        </div>
      </div>
      {modal === 'leave' && canEdit && (
        <TimesheetLeaveModal
          timesheet={timesheet}
          types={options.leaveTypes}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'overtime' && canEdit && (
        <TimesheetOvertimeModal
          timesheet={timesheet}
          entry={editing}
          types={options.overtimeTypes}
          pending={pending}
          onSave={onOvertimesChange}
          onClose={() => setModal(null)}
        />
      )}
      {deleting && canEdit && (
        <ConfirmationModal
          title="Supprimer ces heures supplémentaires ?"
          description={`${extraDate(deleting.date)} · ${deleting.startTime} à ${deleting.endTime}. Les autres plages horaires seront conservées.`}
          confirmLabel="Supprimer les heures"
          pending={busy}
          error={deleteError || undefined}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            if (busy) return;
            setRemoving(true);
            void onOvertimesChange(
              timesheet.overtimes.filter((entry) => entry.id !== deleting.id).map(overtimeInput),
            )
              .then(() => setDeleting(null))
              .catch((cause: unknown) =>
                setDeleteError(
                  cause instanceof Error ? cause.message : 'La suppression a échoué. Réessayez.',
                ),
              )
              .finally(() => setRemoving(false));
          }}
        />
      )}
    </section>
  );
};
